import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import toast from 'react-hot-toast';
import './ep.css';
import { epApi, showError } from './api';
import DiscoverPage from './DiscoverPage';
import { DECISION_LABEL, deadlineText, md, programName, STAGES } from './format';
import { useLoad } from './hooks';
import { PlanView, ProgramCard, ProgramList, TrackedList, TrackedProgram } from './types';

type Viewer = 'counselor' | 'parent';
const STAGE_CHIP: Record<string, string> = { researching: '', applying: 'a', submitted: 'ok', decision: 'ok' };

/**
 * Programs tab on the counselor and parent student view (ReviewStudent).
 * Shows the student's programs with step counts and each step (no percentages),
 * and the same Discover search and filters, set up for the student.
 * Counselors suggest programs (counselor only, like colleges); parents send one with a nudge.
 */
export default function StudentProgramsTab({ studentId, studentName, viewer }: { studentId: number; studentName: string; viewer: Viewer }) {
  const [tab, setTab] = useState<'theirs' | 'find'>('theirs');
  const name = studentName || 'the student';
  const tracked = useLoad<TrackedList>(() => epApi.tracked(studentId), [studentId]);
  const lists = useLoad<ProgramList[]>(() => epApi.lists(studentId), [studentId]);
  const [suggesting, setSuggesting] = useState<ProgramCard | null>(null);
  const [sent, setSent] = useState<Set<number>>(new Set());

  const mine = (lists.data ?? []).filter((l) => l.can_edit);
  const suggestedIds = new Set<number>([...mine.flatMap((l) => l.items.map((i) => i.program?.id ?? 0)), ...sent]);

  const nudge = async (p: ProgramCard) => {
    try {
      await epApi.nudge(studentId, p.id);
      setSent(new Set(sent).add(p.id));
      toast.success(`Sent to ${name}`);
    } catch (e) {
      showError(e);
    }
  };

  const cardAction = (p: ProgramCard) =>
    viewer === 'counselor' ? (
      <button type="button" className="btn sm primary" disabled={suggestedIds.has(p.id)} onClick={() => setSuggesting(p)}>
        {suggestedIds.has(p.id) ? 'Suggested' : `Suggest to ${name}`}
      </button>
    ) : (
      <button type="button" className="btn sm primary" disabled={sent.has(p.id)} onClick={() => nudge(p)}>
        {sent.has(p.id) ? 'Sent' : `Send to ${name}`}
      </button>
    );

  return (
    <div className="ep ep-embedded">
      <div className="row" role="tablist" aria-label="Programs" style={{ marginBottom: 16 }}>
        <button type="button" role="tab" aria-selected={tab === 'theirs'} className={`chipb ${tab === 'theirs' ? 'on' : ''}`} onClick={() => setTab('theirs')}>
          {`${name}'s programs`}
        </button>
        <button type="button" role="tab" aria-selected={tab === 'find'} className={`chipb ${tab === 'find' ? 'on' : ''}`} onClick={() => setTab('find')}>
          Find programs
        </button>
      </div>

      {tab === 'theirs' && (
        <>
          <section className="card" aria-label={`${name}'s programs`}>
            <h2>{`${name}'s programs`}</h2>
            {tracked.loading && !tracked.data ? (
              <p className="muted" style={{ marginTop: 12 }}>Loading</p>
            ) : (tracked.data?.tracked ?? []).length === 0 ? (
              <p className="body" style={{ marginTop: 12 }}>
                {name} is not tracking any programs yet.{' '}
                <button type="button" className="linkish" onClick={() => setTab('find')}>
                  {viewer === 'counselor' ? 'Find one to suggest' : 'Find one to send'}
                </button>
              </p>
            ) : (
              tracked.data!.tracked.map((p) => <TrackedForViewer key={p.program_id} p={p} studentId={studentId} studentName={name} />)
            )}
          </section>
          {viewer === 'counselor' && <SuggestionsSent lists={mine} name={name} />}
        </>
      )}

      {tab === 'find' && (
        <DiscoverPage
          embedded
          forStudentName={name}
          storageKey={`ep.discover.${viewer}.${studentId}`}
          loadEligibility={() => epApi.studentEligibility(studentId).catch(() => null)}
          openHref={(id) => `/summer-programs/programs?id=${id}&student=${studentId}&name=${encodeURIComponent(name)}`}
          cardAction={cardAction}
        />
      )}

      {suggesting && (
        <SuggestModal
          program={suggesting}
          name={name}
          onClose={() => setSuggesting(null)}
          onSend={async (note) => {
            try {
              const list = mine.find((l) => l.is_suggestion);
              const listId = list ? list.id : (await epApi.createList(studentId, 'Suggested programs')).id;
              await epApi.addToList(listId, suggesting.id, note);
              setSent(new Set(sent).add(suggesting.id));
              setSuggesting(null);
              lists.reload();
              toast.success(`Suggested to ${name}. ${name} gets a notice and can add it or say not for me.`);
            } catch (e) {
              showError(e);
            }
          }}
        />
      )}
    </div>
  );
}

function TrackedForViewer({ p, studentId, studentName }: { p: TrackedProgram; studentId: number; studentName: string }) {
  const history = useHistory();
  const [open, setOpen] = useState(false);
  const [plan, setPlan] = useState<PlanView | null>(null);
  const toggle = async () => {
    if (!open && !plan) {
      try {
        setPlan(await epApi.plan(studentId, p.program_id));
      } catch (e) {
        showError(e);
        return;
      }
    }
    setOpen(!open);
  };
  const stage = STAGES.find(([k]) => k === p.stage)?.[1] ?? p.stage;
  return (
    <div className="trow" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
      <div className="between" style={{ alignItems: 'center' }}>
        <div style={{ minWidth: 0 }}>
          <div className="row" style={{ gap: 8 }}>
            <span style={{ fontWeight: 500, fontSize: 15 }}>{p.name}</span>
            <span className={`chip ${STAGE_CHIP[p.stage]}`}>{p.decision ? DECISION_LABEL[p.decision] : stage}</span>
          </div>
          {p.institution && <div className="muted small" style={{ fontWeight: 500, marginTop: 2 }}>{p.institution}</div>}
          <div className="body small" style={{ marginTop: 6 }}>
            {`${p.done_count} of ${p.total_count} steps done`}
            {p.next_step ? ` · Next: ${p.next_step.title}${p.next_step.date ? `, ${md(p.next_step.date)}` : ''}` : ''}
          </div>
          <div className="muted small" style={{ marginTop: 2 }}>{deadlineText(p.deadline_kind, p.next_deadline, p.last_year_deadline_date)}</div>
        </div>
        <div className="row" style={{ flexWrap: 'nowrap' }}>
          <button type="button" className="btn sm" aria-expanded={open} onClick={toggle}>{open ? 'Hide steps' : 'Show steps'}</button>
          <button type="button" className="btn sm" onClick={() => history.push(`/summer-programs/programs?id=${p.program_id}&student=${studentId}&name=${encodeURIComponent(studentName)}`)}>
            View
          </button>
        </div>
      </div>
      {open && plan && (
        <ul style={{ listStyle: 'none', margin: '10px 0 0', padding: 0 }}>
          {plan.checklist.filter((t) => t.status !== 'not_applicable').map((t) => (
            <li key={t.id} className="between" style={{ padding: '8px 0', borderTop: '1px solid var(--ep-line)' }}>
              <span className={t.status === 'done' ? 'muted' : ''} style={{ textDecoration: t.status === 'done' ? 'line-through' : 'none' }}>
                {t.status === 'done' ? '✓ ' : ''}
                {t.title}
              </span>
              <span className="small" style={{ whiteSpace: 'nowrap', color: t.status === 'done' ? 'var(--ep-muted)' : 'var(--ep-body)' }}>
                {t.status === 'done' ? 'Done' : t.date ? md(t.date) : ''}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SuggestionsSent({ lists, name }: { lists: ProgramList[]; name: string }) {
  const items = lists.filter((l) => l.is_suggestion).flatMap((l) => l.items.filter((i) => i.program));
  if (!items.length) return null;
  const status = (i: (typeof items)[number]) =>
    i.student_response === 'not_for_me' ? ['Not for me', 'w'] : i.in_student_programs || i.student_response === 'added' ? [`In ${name}'s programs`, 'ok'] : ['Waiting', ''];
  return (
    <section className="card" aria-label="Your suggestions">
      <h2>Your suggestions</h2>
      {items.map((i) => {
        const [label, tone] = status(i);
        return (
          <div key={i.id} className="trow">
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 500 }}>{programName(i.program!)}</div>
              {i.note && <div className="body small">{i.note}</div>}
            </div>
            <span className={`chip ${tone}`}>{label}</span>
          </div>
        );
      })}
    </section>
  );
}

function SuggestModal({ program, name, onClose, onSend }: { program: ProgramCard; name: string; onClose: () => void; onSend: (note: string) => void }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  return (
    <div className="ep-modal-bg" role="dialog" aria-modal="true" aria-labelledby="ep-sg-title" onClick={onClose}>
      <div className="ep-modal ep" style={{ padding: 24, minHeight: 0 }} onClick={(e) => e.stopPropagation()}>
        <h2 id="ep-sg-title">{`Suggest to ${name}`}</h2>
        <p className="muted" style={{ marginTop: 4 }}>{programName(program)}</p>
        <label className="lbl" htmlFor="ep-sg-note" style={{ marginTop: 14 }}>A note for {name} (optional)</label>
        <textarea id="ep-sg-note" className="in" autoFocus maxLength={500} placeholder="Why it fits, e.g. paid lab work close to home" value={note} onChange={(e) => setNote(e.target.value)} />
        <div className="foot">
          <button
            type="button"
            className="btn primary"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              await onSend(note.trim());
              setBusy(false);
            }}
          >
            Send suggestion
          </button>
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
